import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Users,
  CalendarRange,
  ShoppingCart,
  RotateCcw,
  Receipt,
  BarChart3,
  Settings,
  UserCog,
  LogOut,
  ScrollText,
  Wallet,
  ShieldCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCanAccess } from '@/components/auth/ProtectedRoute';
import { cn } from '@/lib/format';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/pos', label: 'POS', icon: ShoppingCart },
  { to: '/inventory', label: 'Inventory', icon: Package },
  { to: '/customers', label: 'Customers', icon: Users },
  { to: '/events', label: 'Events', icon: CalendarRange },
  { to: '/returns', label: 'Returns', icon: RotateCcw },
  { to: '/transactions', label: 'Transactions', icon: Receipt },
  { to: '/piutang', label: 'Piutang', icon: Wallet },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/approvals', label: 'Approval', icon: ShieldCheck },
  { to: '/settings', label: 'Settings', icon: Settings },
  { to: '/users', label: 'Users', icon: UserCog },
  { to: '/audit-logs', label: 'Audit logs', icon: ScrollText },
];

export function Sidebar() {
  const { user, signOut } = useAuth();
  const canAccess = useCanAccess();
  const navigate = useNavigate();

  if (!user) return null;

  // One source of truth with the route gate: an entry is shown exactly when
  // canAccess() would let you open it. The old `ownerOnly` flag was a second
  // list that could — and did — disagree with the first.
  const visible = NAV_ITEMS.filter((item) => canAccess(item.to));

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 z-30 bg-surface border-r border-line">
      <div className="h-16 flex items-center px-6 border-b border-line">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-primary flex items-center justify-center text-white font-display font-semibold">
            O
          </div>
          <div>
            <p className="text-body font-display font-semibold text-ink leading-none">
              Ophth POS
            </p>
            <p className="text-caption text-ink-subtle mt-0.5">Event Edition</p>
          </div>
        </div>
      </div>

      <nav data-tour="sidebar-nav" className="flex-1 overflow-y-auto p-4 space-y-1">
        {visible.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded text-body font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-ink-subtle hover:bg-line/30 hover:text-ink',
              )
            }
          >
            <item.icon size={18} strokeWidth={1.75} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-line">
        <div className="px-3 py-2 mb-2">
          <p className="text-small font-medium text-ink truncate">{user.displayName}</p>
          <p className="text-caption text-ink-subtle truncate">{user.email}</p>
          <span className="chip mt-2 capitalize">{user.role}</span>
        </div>
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-3 px-3 py-2 rounded text-body text-ink-subtle hover:bg-line/30 hover:text-ink transition-colors"
        >
          <LogOut size={18} strokeWidth={1.75} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
